import { type FC, useCallback, useEffect, useRef, useState } from "react";
import { GlobeIcon, ShieldOffIcon, XIcon } from "lucide-react";
import { useTabs } from "@/contexts/tabsContext";
import { usePreviewBrowser } from "./usePreviewBrowser";
import { usePreviewBrowserContext } from "./PreviewBrowserContext";
import { PreviewBrowserToolbar } from "./PreviewBrowserToolbar";
import { buildPreviewUrl, getPreviewDomain } from "@/components/filePreview/viewers/previewUrl";
import { useFilePreviewReload } from "@/components/filePreview/useFilePreviewReload";

/**
 * Mini-browser tab for previewing HTML workspace assets in an isolated subdomain.
 * The iframe owns its own history; this component provides browser chrome and
 * observes navigation via postMessage from the injected script.
 */
export const PreviewBrowserTab: FC<{ workspaceId: string; tabId: string }> = ({ workspaceId, tabId }) => {
  const { currentPath, emitNavigated, setIframe, reload } = usePreviewBrowser(workspaceId);
  const ctx = usePreviewBrowserContext();
  const tabs = useTabs();
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [blockedUrl, setBlockedUrl] = useState<string | null>(null);

  // Initial URL — set imperatively once currentPath first becomes non-empty.
  // src is NEVER a React prop, so re-renders don't reload the iframe.
  const hasSetInitialSrc = useRef(false);
  useEffect(() => {
    if (hasSetInitialSrc.current || !currentPath || !iframeRef.current) return;
    const filePath = `${workspaceId}${currentPath}`;
    iframeRef.current.src = buildPreviewUrl(workspaceId, filePath, getPreviewDomain());
    hasSetInitialSrc.current = true;
  }, [currentPath, workspaceId]);

  // Register / unregister the iframe with the provider via a ref callback.
  // The iframe only renders when currentPath is truthy, so a mount-time effect
  // would capture null. A ref callback fires whenever the element mounts/unmounts.
  const setIframeRef = useCallback((el: HTMLIFrameElement | null) => {
    iframeRef.current = el;
    setIframe(el);
  }, [setIframe]);

  // Driver navigate events → swap iframe src (already-open tab).
  // Deliberately a SECOND subscription to "navigate" (distinct from the hook's,
  // which only updates URL-bar React state). Merging them would put the src
  // change in a React state update path and cause re-render/src-reassign reloads.
  useEffect(() => {
    const onNavigate = (filePath: string) => {
      if (iframeRef.current) {
        iframeRef.current.src = buildPreviewUrl(workspaceId, filePath, getPreviewDomain());
      }
    };
    ctx.events.subscribe(workspaceId, "navigate", onNavigate);
    return () => ctx.events.unsubscribe(workspaceId, "navigate", onNavigate);
  }, [workspaceId, ctx]);

  // postMessage listener — observe in-iframe navigations.
  // NOTE: The iframe is sandboxed without allow-same-origin, so event.origin is "null"
  // (opaque origin). We validate by checking the event source is our iframe's contentWindow
  // instead of checking origin.
  useEffect(() => {
    const handler = (event: MessageEvent) => {
      // 1. Validate source is our iframe (origin is "null" for sandboxed iframes)
      if (event.source !== iframeRef.current?.contentWindow) return;
      // 2. Route by message type
      if (event.data?.type === "preview-navigate") {
        if (typeof event.data.path !== "string") return;
        // Emit to the bus → updates URL bar state, and persist for reloads
        emitNavigated(event.data.path);
        ctx.reportPath(workspaceId, event.data.path);
        setBlockedUrl(null); // clear any previous blocked notice on successful nav
        return;
      }
      if (event.data?.type === "preview-blocked") {
        if (typeof event.data.url === "string") setBlockedUrl(event.data.url);
        return;
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [workspaceId, emitNavigated, ctx]);

  // Hot reload — incrementing key from useFilePreviewReload; reload on change.
  const reloadKey = useFilePreviewReload(workspaceId);
  const prevReloadKeyRef = useRef(reloadKey);
  useEffect(() => {
    if (reloadKey !== prevReloadKeyRef.current) {
      prevReloadKeyRef.current = reloadKey;
      reload();
    }
  }, [reloadKey, reload]);

  // Tab title — current file name.
  useEffect(() => {
    const fileName = currentPath.split("/").pop() || "Preview";
    tabs.setTabMetadata(tabId, { title: fileName, icon: <GlobeIcon className="w-3.5 h-3.5" /> });
  }, [currentPath, tabId, tabs]);

  return (
    <div className="flex flex-col h-full">
      <PreviewBrowserToolbar workspaceId={workspaceId} />
      {blockedUrl && (
        <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-950/50 border-b border-amber-800/50 text-amber-200 text-xs">
          <ShieldOffIcon className="w-3.5 h-3.5 shrink-0" />
          <span className="flex-1 truncate">
            External navigation blocked for security: <span className="text-amber-400">{blockedUrl}</span>
          </span>
          <button
            type="button"
            onClick={() => setBlockedUrl(null)}
            className="p-0.5 rounded hover:bg-amber-900/50 shrink-0"
            title="Dismiss"
          >
            <XIcon className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
      {currentPath ? (
        <iframe
          ref={setIframeRef}
          sandbox="allow-scripts allow-forms allow-popups"
          className="flex-1 w-full border-0"
          title="Preview Browser"
        />
      ) : (
        <div className="flex items-center justify-center flex-1">
          <p className="text-sm text-zinc-500">Open an HTML file from the assets panel</p>
        </div>
      )}
    </div>
  );
};
